export type ServiceVisual = 'calendar' | 'map' | 'ads' | 'analytics' | 'radar' | 'moodboard'

export type BoardReading = 'pressure' | 'calendar' | 'bars' | 'radar' | 'dial' | 'swatches'

export type Service = {
    slug: string
    index: string
    title: string
    short: string
    lead: string
    description: Array<string>
    deliverables: Array<string>
    outcome: { value: string, label: string }
    visual: ServiceVisual
    board: BoardReading
    station: string
    reading: string
}

export const services: Array<Service> = [
    {
        slug: 'forecasting',
        index: '01',
        title: 'Forecast & Strategy',
        short: 'We read where your category is heading and plan the posts that get there first.',
        lead: 'A plan with a point of view, written before anyone picks up a camera.',
        description: [
            'Every engagement starts with a forecast. We read your accounts, your competitors and the wider feed, then write down what your audience is about to care about and how you can be the brand they think of first.',
            'The result is a short, opinionated plan: the two or three platforms worth your time, the recurring series you will own, and the one number we will judge it all by.'
        ],
        deliverables: [
            'A category forecast, refreshed every quarter',
            'Audience and competitor read',
            'Two or three recurring series you can own',
            'Platform roles and a posting rhythm',
            'A 90 day plan with one north star number',
            'Quarterly forecast reviews'
        ],
        outcome: { value: '90', label: 'day plan with one number that matters' },
        visual: 'map',
        board: 'pressure',
        station: 'STN 01 · 51.5N',
        reading: 'Pressure rising'
    },
    {
        slug: 'content-studio',
        index: '02',
        title: 'Content Studio',
        short: 'One shoot day becomes a month of posts. Planned, filmed and edited for each app.',
        lead: 'Made for the feed it lives in, never resized for it.',
        description: [
            'Our studio plans, shoots and edits in calm monthly batches. Short videos, carousels, stories and photos, each one cut for the app it is going to and written with a job in mind.',
            'You approve ideas before we shoot and edits before they go out. Nothing is posted in a panic and nothing goes out without a reason.'
        ],
        deliverables: [
            'Monthly ideas list and shot plan',
            'Half-day and full-day batch shoots',
            'Short video editing with on-screen text',
            'Carousels, stories and photo posts',
            'App-native versions of every idea',
            'A shared library of every file'
        ],
        outcome: { value: '30+', label: 'posts a month on the Growth plan' },
        visual: 'calendar',
        board: 'calendar',
        station: 'STN 02 · 0.1W',
        reading: 'Calendar filling'
    },
    {
        slug: 'paid-social',
        index: '03',
        title: 'Paid Social',
        short: 'Ads that do not feel like ads. Tested small, cut early, scaled when they earn it.',
        lead: 'Creative and numbers in the same room, every week.',
        description: [
            'We make ad creative in volume, test hooks in small batches and move budget towards whatever beats the organic baseline. Losers get cut within days, winners get more fuel.',
            'Every pound is visible in one place, with a short weekly note on what we tested, what we learned and what happens next.'
        ],
        deliverables: [
            'Campaign setup and audience structure',
            'Creator-led ad production',
            'Ten new hooks tested every week',
            'Budget pacing and bidding',
            'Conversion tracking you can trust',
            'A weekly one page note'
        ],
        outcome: { value: '10', label: 'new hooks tested every week' },
        visual: 'ads',
        board: 'bars',
        station: 'STN 03 · ROAS',
        reading: 'Bars climbing'
    },
    {
        slug: 'trend-desk',
        index: '04',
        title: 'Trend Desk',
        short: 'A radar on sounds, formats and conversations, with a 48 hour turnaround.',
        lead: 'Early to the trends that fit. Happily late to the ones that do not.',
        description: [
            'Our desk watches sounds, formats, creators and conversations across every major app, every day. You get a short weekly brief on what is rising in your corner of the internet and which moments are worth joining.',
            'When something genuinely fits your brand, we go from rising trend to finished post in 48 hours. When it does not, we tell you to sit it out.'
        ],
        deliverables: [
            'A weekly signal brief for your category',
            'Sound and format watchlists',
            'Rapid response posts within 48 hours',
            'Creator and community scouting',
            'Competitor monitoring',
            'A seasonal and cultural calendar'
        ],
        outcome: { value: '48h', label: 'from rising trend to posted' },
        visual: 'radar',
        board: 'radar',
        station: 'STN 04 · LIVE',
        reading: 'Signal detected'
    },
    {
        slug: 'reading-the-room',
        index: '05',
        title: 'Reading the Room',
        short: 'Replies, saves and sales, read every week and turned into decisions.',
        lead: 'A short list of numbers that actually predict growth.',
        description: [
            'The apps give you hundreds of numbers. We pick the handful that predict growth for your business, track them live and explain them in plain English.',
            'Every report ends in decisions: what gets a sequel, what gets quietly retired and what we will test next month.'
        ],
        deliverables: [
            'A measurement plan built on your goals',
            'A live dashboard across every app',
            'A one page monthly report',
            'Format and creative reviews',
            'Comment and sentiment reads',
            'A quarterly growth workshop'
        ],
        outcome: { value: '1', label: 'page report, ending in decisions' },
        visual: 'analytics',
        board: 'dial',
        station: 'STN 05 · READ',
        reading: 'Mood: warm'
    },
    {
        slug: 'look-and-voice',
        index: '06',
        title: 'Look & Voice',
        short: 'A visual and verbal system so your posts are recognisable before anyone reads the handle.',
        lead: 'One brand, one point of view, recognisable in a single frame.',
        description: [
            'We build the system behind your feed: palette, type, templates, photo direction and a voice guide with a proper no list. It is the difference between a feed and a pile of posts.',
            'Your team and your creators get tools they can use with confidence, so the brand still sounds like you on the weeks we are not holding the phone.'
        ],
        deliverables: [
            'A visual identity built for phones',
            'Templates and a graphic system',
            'Photo and video direction',
            'A voice guide with a no list',
            'Grid planning and art direction',
            'Guidelines for creators'
        ],
        outcome: { value: '9', label: 'posts planned as a grid before anything goes live' },
        visual: 'moodboard',
        board: 'swatches',
        station: 'STN 06 · TONE',
        reading: 'Colours settling'
    }
]

export const addOns: Array<{ title: string, detail: string, price: string }> = [
    { title: 'Creator casting', detail: 'People who fit your brand, briefed properly and paid fairly.', price: 'from £900' },
    { title: 'Community care', detail: 'Replies, DMs and moderation in your voice, seven days a week.', price: 'from £600' },
    { title: 'Podcast cut-downs', detail: 'Long conversations turned into short, shareable moments.', price: 'from £450' },
    { title: 'Voice guide', detail: 'A no list, a yes list and before and after rewrites.', price: 'from £1,200' },
    { title: 'Account check-up', detail: 'A deep read of every channel and a 30 day fix list.', price: 'from £750' },
    { title: 'Launch weeks', detail: 'Teasers, countdowns and launch day posts in one sprint.', price: 'from £2,400' },
    { title: 'Live shows', detail: 'Planning, hosting help and highlight clips for livestreams.', price: 'from £800' }
]

export type ComparisonRow = {
    label: string
    us: string | boolean
    alone: string | boolean
}

export const comparison: Array<ComparisonRow> = [
    { label: 'A forecast before anything gets made', us: true, alone: false },
    { label: 'A month of content from one shoot day', us: true, alone: false },
    { label: 'Editors who live inside every app', us: true, alone: false },
    { label: 'A weekly signal brief for your category', us: true, alone: false },
    { label: 'Hook testing on paid social', us: true, alone: false },
    { label: 'Reports that end in decisions', us: true, alone: false },
    { label: 'Your evenings back', us: true, alone: false },
    { label: 'Monthly cost', us: 'From £1,450', alone: '£9k+ for a small in-house team' }
]
